import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { useSetAtom } from 'jotai';

import { hasAudioActivityAtom } from '../../atoms/recording';

import { LargeMicVisualizer } from './LargeMicVisualizer';
import { MicLoadingState } from './MicLoadingState';
import { MiniMicVisualizer } from './MiniMicVisualizer';

type VoiceCardProps = {
  stream: MediaStream | null;
  isRecordingSessionActive?: boolean;
  onAudioActivityChange?: (hasActivity: boolean) => void;
  iconSize?: 'sm' | 'md' | 'lg';
};

export const VoiceCard: FC<VoiceCardProps> = ({
  stream,
  isRecordingSessionActive,
  onAudioActivityChange,
  iconSize = 'md',
}) => {
  // local state
  const [hasAudioActivity, setHasAudioActivity] = useState(false);
  const [hasDebouncedAudioActivity, setDebouncedAudioActivity] =
    useState(false);
  const [isAudioAnalysisReady, setIsAudioAnalysisReady] = useState(false);

  // atoms - set global audio activity state for canRecord
  const setGlobalAudioActivity = useSetAtom(hasAudioActivityAtom);

  // The library creates new MediaStream wrapper objects on every render,
  // but the underlying MediaStreamTrack is stable - use it directly
  const audioTrack = stream?.getAudioTracks()[0];
  const isTrackLive = audioTrack?.readyState === 'live';
  const isStreamActive = stream?.active === true;

  // Reset global audio activity when component unmounts.
  // This is intentional because hasAudioActivityAtom is a PRE-RECORDING safety gate:
  // - VoiceCard detects audio activity to verify the mic is working BEFORE recording starts
  // - Once recording starts, ConsentFlow (which uses canRecord) is hidden and replaced
  //   with recording controls, so hasAudioActivity is no longer checked
  // - If VoiceCard unmounts (e.g., sheet closed during pre-recording), we reset the atom
  //   because we can no longer verify audio is being captured
  // - When VoiceCard remounts, it will re-verify audio before allowing recording to start
  useEffect(() => {
    return () => {
      setGlobalAudioActivity(false);
    };
  }, [setGlobalAudioActivity]);

  // Debounce audio activity to prevent constant switching
  useEffect(() => {
    const updateActivity = (active: boolean) => {
      setDebouncedAudioActivity(active);
      onAudioActivityChange?.(active);
      // Update global atom to enable/disable recording buttons
      setGlobalAudioActivity(active);
    };

    if (!audioTrack || !isTrackLive || hasAudioActivity) {
      updateActivity(!!audioTrack && !!isTrackLive && hasAudioActivity);
      return;
    }

    const timeoutId = setTimeout(() => updateActivity(false), 5000);
    return () => clearTimeout(timeoutId);
  }, [
    audioTrack,
    isTrackLive,
    hasAudioActivity,
    onAudioActivityChange,
    setGlobalAudioActivity,
  ]);

  // Audio activity detection using Web Audio API
  useEffect(() => {
    // if there's no active audio track, reset the state and exit early
    if (!audioTrack || !isTrackLive) {
      setHasAudioActivity(false);
      setIsAudioAnalysisReady(false);
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const mediaStream = new MediaStream([audioTrack]);
    const source = audioContext.createMediaStreamSource(mediaStream);
    source.connect(analyser);
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let analysisCount = 0;
    let isCleanedUp = false;

    const checkActivity = () => {
      // Exit early if cleanup has been called
      if (isCleanedUp || !analyser) return;

      // Get current frequency data from the microphone
      analyser.getByteFrequencyData(dataArray);
      const rms = Math.sqrt(
        dataArray.reduce((sum, val) => sum + val * val, 0) / dataArray.length,
      );
      setHasAudioActivity(rms > 10);

      if (++analysisCount >= 5) {
        setIsAudioAnalysisReady(true);
      }
    };

    const interval = setInterval(checkActivity, 50);

    return () => {
      isCleanedUp = true;
      clearInterval(interval);
      // Disconnect audio nodes before closing context
      source.disconnect();
      analyser.disconnect();
      // Close the audio context to free system resources
      audioContext.close().catch(() => {
        // Ignore errors if already closed
      });
    };
  }, [audioTrack, isTrackLive]);

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-gap-1 vai-py-1">
      {/* Microphone level indicator */}
      <div className="vai-flex vai-items-center vai-justify-center">
        {isRecordingSessionActive ? (
          <MiniMicVisualizer stream={stream} barCount={24} />
        ) : !isAudioAnalysisReady ? (
          <MicLoadingState iconSize={iconSize} />
        ) : (
          <LargeMicVisualizer
            isActive={isStreamActive && hasDebouncedAudioActivity}
            iconSize={iconSize}
          />
        )}
      </div>
    </div>
  );
};
